import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialogConfig } from '@angular/material';
import { NagPlacenameResoComponent, NagPlacenameResolutionData, NagPlacenameResolutionOutput } from 'app/components/content-config/config-nag/nag-placename-reso/nag-placename-reso.component';
import { CountyNeighborOption } from 'app/components/feat-edit-cell/feat-edit-cell.component';
import { ActivityService } from 'app/services/activity.service';
import { AgencyInfoService, NguidPattern } from 'app/services/agency-info.service';
import { AppinfoService, ColumnNagParam, LbColumnType } from 'app/services/appinfo.service';
import { AttrIndexService } from 'app/services/attr-index.service';
import { CommonFunctionService } from 'app/services/common-function.service';
import { CommonModalService } from 'app/services/common-modal.service';
import { DomainService, GivColumnDomain } from 'app/services/domain.service';
import { EditService } from 'app/services/edit.service';
import { LayerCategoryStandardSchema } from 'app/services/field-mapping.service';
import { ColumnFilter, GfoService } from 'app/services/gfo.service';
import { Gto } from 'app/services/gto.service';
import { LayerQcService } from 'app/services/layer-qc.service';
import { AppLayer } from 'app/services/layer.service';
import { MainDataService } from 'app/services/main-data.service';
import { NagService } from 'app/services/nag.service';
import { QaResolverFeed_Domain, QaResolverFeed_DomainValue, ResoCenterCol_Domain, ResolutionCenterDomainService } from 'app/services/resolution-center-domain.service';
import { QaRuleCode } from 'app/services/resolution-center.service';
import { TooltipHelpService } from 'app/services/tooltip-help.service';
import { UserService } from 'app/services/user.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

interface DomainFeedItem {
	rule: {
		code: QaRuleCode;
		descShort: string;
		descLong: string;
	};
	count: number;
	cols: ResoCenterCol_Domain[];
	isExpand?: boolean;
}

@Component({
  selector: 'app-domain-resolution-inspector',
  templateUrl: './domain-resolution-inspector.component.html',
  styleUrls: ['./domain-resolution-inspector.component.css', '../resolution-inspector/resolution-inspector.component.css']
})
export class DomainResolutionInspectorComponent implements OnInit, OnDestroy {

	feedItemList: DomainFeedItem[];

	selectedColumn: {
		col: ResoCenterCol_Domain;
		rule: QaRuleCode;
		currentValueList: QaResolverFeed_DomainValue[];
		domain?: GivColumnDomain;
		nagParam?: ColumnNagParam;
	};
	colType: LbColumnType;
	isCountyCol?: boolean;
	isStateCol?: boolean;
	stateNeighborList: string[];
	countyNeighborList: CountyNeighborOption[];
	
	ruleCode: QaRuleCode;

	nguidPattern: NguidPattern;
	discrepancyId;

	domainsByColumn: {
		[prop: string]: GivColumnDomain;
	}

	hasChanged: boolean;

	showNagOperator: boolean = true;

	nguidStandardName: string;

	private countyList: {stnId; name;}[];

	private qaResolverFeeds: QaResolverFeed_Domain[] = [];

	private layerId;

	private mouseoverCodeTimeout;

	private resoFeedSub: Subscription;

	constructor(
		private toastr: ToastrService,
		private activityService: ActivityService,
		private agencyInfoService: AgencyInfoService,
		private appinfoService: AppinfoService,
		private attrIndexService: AttrIndexService,
		private commonService: CommonFunctionService,
		private domainService: DomainService,
		private editService: EditService,
		private gfoService: GfoService,
		private mainDataService: MainDataService,
		private modalService: CommonModalService,
		private nagService: NagService,
		private resoCenterDomainService: ResolutionCenterDomainService,
		private qcService: LayerQcService,
		private tooltipHelpService: TooltipHelpService,
		private userService: UserService
	) { }

	discard() {
		if (this.selectedColumn) {
			this.selectedColumn.currentValueList.forEach(i => i.newValue = undefined);
		}
		this.hasChanged = false;
	}
	async commit() {
		const spinnerId = this.commonService.getRandomId();
		this.commonService.setLoadingSpinner(true, spinnerId);
		try {
			let changedCols: {
				[prop: string]: {
					val;
					isExpression?: boolean;
				}
			} = {};
	
			const layer = this.activityService.selectedLayerBS.getValue().layer;
			const layerCate = layer.category;
			const sourceTableName = layerCate.tableName;
			const prefix = layerCate.colPrefix;
			const primKeyCol = this.editService.getPrimKeyCol(prefix);
			const colProp = this.selectedColumn.col.prop;
			const generalColType = this.appinfoService.findGeneralCol(this.selectedColumn.col.lbType);
			const cols = this.editService.getUpdateColsInSourceTable(sourceTableName, prefix);
			const colsWithName = cols.map(c => {
				return {
					prop: c,
					name: this.appinfoService.columnMap[c] ? this.appinfoService.columnMap[c].heading : ''
				}
			});
	
			let gtoAll: Gto[] = [];
			this.selectedColumn.currentValueList.forEach(i => {
				changedCols = {};
				if (this.selectedColumn.col.standardName === this.nguidStandardName) {
					const split = '__splitString__';
					let nguidClause = this.agencyInfoService.computeNguid(layerCate.code, split);
					const splits = nguidClause.split(split);
					nguidClause = `'${splits[0]}' || ${layerCate.colPrefix}_globalid || '${splits[1]}'`;
					changedCols[this.selectedColumn.col.prop] = {
						val: nguidClause,
						isExpression: true
					};
				} else {
					if ((i.newValue || i.newValue === 0) && i.isValid) {
						const format = this.editService.formatClauseValForGto(i.newValue, generalColType, colsWithName);
						changedCols[colProp] = {
							val: format.val,
							isExpression: format.isExpression 
						};
					}
				}		
				let filter: ColumnFilter;
				if (i.value === null) {
					filter = this.gfoService.getFilter(colProp, this.appinfoService.filterOperators['is null'].name, null);
				} else {
					filter = this.gfoService.getFilter(colProp, this.appinfoService.filterOperators.eq.name, i.value);
				}

				let whereClause = this.editService.getWhereClauseFromColumnFilters([filter]);
				// Add layer id
				const layerIdWhere = `${prefix}_${this.attrIndexService.layerIdColumn} = ${layer.id}`;
				whereClause = this.editService.addToWhereClause(whereClause, layerIdWhere);
				// Exclude deleted record
				const notDeletedWhere = `${prefix}_${this.attrIndexService.tranxDeleteColName} IS NULL`;
				whereClause = this.editService.addToWhereClause(whereClause, notDeletedWhere);

				const gtos = this.editService.getUpdateGtosBatch(sourceTableName, whereClause, changedCols, cols, primKeyCol, prefix).gtos;
				gtoAll = gtoAll.concat(gtos);

			});
	
			if (gtoAll.length) {
				await this.editService.commitGtoAndQcForBach(gtoAll, [this.selectedColumn.col.prop], layer.id, prefix);
				this.discard();
			}
		} catch (err) {
			console.log(err);
		}
		this.commonService.setLoadingSpinner(false, spinnerId);
	}

	async onComputeNguid() {
		await this.commit();
	}

	toggleExpand(item: DomainFeedItem) {
		item.isExpand = !item.isExpand;
		if (item.isExpand) { // Close other expanded items
			this.feedItemList.forEach(i => i.isExpand = i === item);
		}
	}
	async clickColumn(col: ResoCenterCol_Domain, rule: QaRuleCode, force?: boolean) {
		const data = this.resoCenterDomainService.domainValuesByRule;
		if (!data[rule]) {
			data[rule] = {};
		}
		if (!data[rule][col.prop]) {
			data[rule][col.prop] = [];
		}
		if (!data[rule][col.prop].length || force) {
			this.commonService.setLoadingSpinner(true);
			try {
				await this.resoCenterDomainService.getQaResolverFeedsDomainValues(this.layerId, rule, col.standardName, col.prop);
			} catch (err) {
				console.log(err);
			}
			this.commonService.setLoadingSpinner(false);
		}
		this.selectedColumn = {
			col: col,
			rule: rule,
			currentValueList: data[rule][col.prop],
			domain: this.domainsByColumn[col.prop],
			nagParam: this.appinfoService.nagParams.find(c => c.standardName === col.standardName)
		}
		this.colType = this.appinfoService.columnMap[col.prop] ? this.appinfoService.columnMap[col.prop].type : null;
		// Set county and states domain
		this.isCountyCol = col.standardName === this.attrIndexService.countyColNameStandard || col.standardName === this.attrIndexService.countyLeftColStandard || col.standardName === this.attrIndexService.countyRightColStandard;
		this.isStateCol = col.standardName === this.attrIndexService.stateColNameStandard || col.standardName === this.attrIndexService.stateLeftColStandard || col.standardName === this.attrIndexService.stateRightColStandard;
		if (this.isStateCol) {
			this.stateNeighborList = this.userService.getStatesFromNeighborCounties();
			if (this.selectedColumn.domain && this.selectedColumn.domain.type === 'list' && this.selectedColumn.domain.list) {
				this.selectedColumn.domain.list.forEach(l => {
					const idx = this.stateNeighborList.indexOf(l);
					if (idx > -1) {
						this.stateNeighborList.splice(idx, 1);
					}
				})
			}
		}
		if (this.isCountyCol) {
			this.countyNeighborList = this.userService.getNeighborCountiesFromCustomer();
			if (this.selectedColumn.domain && this.selectedColumn.domain.type === 'list' && this.selectedColumn.domain.list) {
				this.selectedColumn.domain.list.forEach(l => {
					const idx = this.countyNeighborList.findIndex(i => i.value === l);
					if (idx > -1) {
						this.countyNeighborList.splice(idx, 1);
					}
				})
			}
		}
		//
		this.ruleCode = rule;

		// Auto fill for discrepancy id
		if (this.selectedColumn.col.standardName.toLowerCase() === this.attrIndexService.discrepancyAgencyIdStandardLower) {
			this.selectedColumn.currentValueList.forEach(i => {
				i.newValue = this.userService.customerInfo.discrepancyId;
			});
		} else if (this.selectedColumn.col.standardName.toLowerCase() === this.attrIndexService.nguidCol.toLowerCase()) {
		}

		this.checkShowNagOperator(rule);
	}
	onEdit() {
		this.hasChanged = true;
	}
	checkCommitDisable() {
		if (this.hasChanged) {
			return false;
		}
		if (this.selectedColumn) {
			const colName = this.selectedColumn.col.standardName.toLowerCase();
			if (colName === this.attrIndexService.discrepancyAgencyIdStandardLower || colName === this.attrIndexService.nguidCol.toLowerCase()) {
				return false;
			}
		}
		return true;
	}
	checkValidity_input(item: QaResolverFeed_DomainValue) {
		const check = this.editService.checkClauseVal(item.newValue, {
			prop: this.selectedColumn.col.prop,
			type: this.appinfoService.findGeneralCol(this.selectedColumn.col.lbType)
		}, this.domainsByColumn);
		item.isValid = check.isValid && !this.checkChangedValueIsSameAsOrigin(item);
	}
	checkValidity_domain(item: QaResolverFeed_DomainValue) { // TODO
		item.isValid = !this.checkChangedValueIsSameAsOrigin(item);
	}
	// If value has changed, check if it's changed to the same value as before // If value hasn't been changed then ignore
	checkChangedValueIsSameAsOrigin(item: QaResolverFeed_DomainValue) {
		return (item.newValue !== undefined && !this.editService.hasValueChanged(item.newValue, item.value))
	}

	// Only update lastTooltips if the mouse is hovering over the cell more than 1 second
	onMouseoverCode(code: string) {
		this.mouseoverCodeTimeout = setTimeout(() => {
			this.tooltipHelpService.lastTooltipsBS.next({
				codes: [code]
			});
		}, this.tooltipHelpService.delayTime);
	}
	onMouseoutCode() {
		if (this.mouseoverCodeTimeout) {
			clearTimeout(this.mouseoverCodeTimeout);
		}
	}

	checkDisableInput() {
		if (this.selectedColumn && this.selectedColumn.col.standardName.toLowerCase() === this.attrIndexService.discrepancyAgencyIdStandardLower) {
			return true;
		}
	}

	async addToNag(i: QaResolverFeed_DomainValue) {
		const nagParam = this.selectedColumn.nagParam;
		if (nagParam) {
			const isPlaceName = nagParam.nctName.startsWith(this.attrIndexService.placenameNagName);
			try {
				if (isPlaceName) {
					const cusCounties = this.userService.customerInfo.cusCounties || [];
					const countyList = this.countyList || await this.agencyInfoService.getCounties();
					this.countyList = countyList.filter(c => cusCounties.indexOf(c.name) > -1);
					//
					const data: NagPlacenameResolutionData = {
						standard: {
							name: i.value
						},
						countyList: this.countyList,
						nctId: nagParam.nctId

					};
					const options: MatDialogConfig = {
						data: data,
						height: '400px',
						width: '600px'
					}
					const result: NagPlacenameResolutionOutput = await this.modalService.openModalWindow(NagPlacenameResoComponent, options);
					if (result) {
						if (result.existingRec) {
							await this.nagService.editStandard_location(nagParam.nctId, result.existingRec.stnId, result.standardName, result.placeTypes, result.county.stnId);
						} else {
							await this.nagService.addStandard_location(nagParam.nctId, result.standardName, result.placeTypes, result.county.stnId);
						}
					} else {
						return;
					}
				} else {
					const standardRecords = await this.nagService.getNagNamesStandardId(i.value, nagParam.nctId, this.userService.customerInfo.givState);
					const standard = standardRecords[0];
					if (standard) {
						await this.nagService.editStandard_street(nagParam.nctId, standard.stnId, i.value, nagParam.source);
					} else {
						await this.nagService.addStandard_street(nagParam.nctId, i.value, nagParam.source);
					}
				}
				this.toastr.success('Successfully added.');
				this.commonService.setLoadingSpinner(true);
				try {
					await this.qcService.qcLayerIncNag([nagParam.nctName]);
				} catch (err) {
					console.log(err);
					this.toastr.error('QC failed.');
				}
				this.commonService.setLoadingSpinner(false);
				// Refresh domain feed
				const selectedCol = this.selectedColumn;
				const layer = this.activityService.selectedLayerBS.getValue().layer;
				const qaResolverFeeds_domain = await this.resoCenterDomainService.getQaResolverFeedsDomain(layer.id, layer.category.code);
				this.resoCenterDomainService.resoFeedDomainBS.next({
					feeds: qaResolverFeeds_domain,
					layerId: layer.id,
					selectedRule: this.ruleCode,
					selectedColProp: selectedCol.col.prop
				});
				// Refresh data
				this.activityService.refreshData.next();
			} catch (err) {
				console.log(err);
			}
		}
	}
	private checkShowNagOperator(ruleCode: QaRuleCode) {
		this.showNagOperator = this.userService.getUserInfo().isAdmin && ruleCode === 'DLS';
	}


	private clearAll() {
		// TODO
	}
	private onFeedChange() {
		this.selectedColumn = null;
		this.colType = null;
		this.feedItemList = this.qaResolverFeeds.map(i => {
			const info = this.mainDataService.qcFilterInfo.getValue();
			const ruleInfo = info.filterOptions.find(o => o.value === i.rule);
			return {
				rule: {
					code: i.rule,
					descShort: ruleInfo ? ruleInfo.name : '',
					descLong: ruleInfo ? ruleInfo.descriptionLong : ''
				},
				count: i.count,
				cols: i.cols,
				isExpand: false
			}
		});
		const layerCate = this.activityService.selectedLayerBS.getValue().layer.category;
		const cols: {prop; standard: LayerCategoryStandardSchema}[] = [];
		// Get all columns insead of just those that are involved, since some domains are dependent on columns that are not involved 
				// this.feedItemList.forEach(i => i.cols.forEach(c => cols.push({
				// 	prop: c.prop,
				// 	standard: layerCate.standardCols.find(s => s.colName === c.standardName)
				// })));
		layerCate.standardCols.forEach(s => {
			cols.push({
				prop: layerCate.colPrefix + '_' + s.colName.toLowerCase(),
				standard: s
			});
		})
		this.domainsByColumn = this.domainService.getColumnDomains(cols, layerCate.code);
	}

	ngOnInit() {
		this.nguidStandardName = this.attrIndexService.nguidCol;
		this.discrepancyId = this.userService.customerInfo.discrepancyId;
		this.nguidPattern = JSON.parse(JSON.stringify(this.userService.customerInfo.nguidPattern));
		this.resoFeedSub = this.resoCenterDomainService.resoFeedDomainBS.subscribe(async data => {
			this.resoCenterDomainService.domainValuesByRule = {};
			if (!data || !data.feeds) {
				this.clearAll();
				return;
			}
			this.layerId = data.layerId;
			this.qaResolverFeeds = data.feeds;
			this.onFeedChange();
			if (data.selectedRule) {
				const expand = this.feedItemList.find(i => i.rule.code === data.selectedRule);
				if (expand && !expand.isExpand) {
					this.toggleExpand(expand);
				}
				if (expand && data.selectedColProp) {
					const col = expand.cols.find(c => c.prop === data.selectedColProp);
					if (col) {
						await this.clickColumn(col, this.ruleCode, true);
					}
				}
			}
		});
	}
	ngOnDestroy(): void {
		this.resoFeedSub.unsubscribe();
	}
}
